import { RootState } from 'configureStore';
import React, { FC, HTMLAttributes, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EventData,
  Joyride,
  Options,
  PartialDeep,
  Step,
  Styles,
} from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { SecondaryNavigation } from 'shamrock-clover-ui/dist/clover/components/SecondaryNavigation/SecondaryNavigation';
import CloverThemeProvider from 'shamrock-clover-ui/dist/clover/Theme/ThemeProvider';
import { logMyLaneButtonClick } from '../features/myLanes/analytics/myLaneAnalytics';
import {
  setJoyrideContext,
  setShowJoyride,
  useGetLanesQuery,
} from '../features/myLanes/state';
import styled from '../theme';
import {
  getCurrentUser,
  getHasBookItNowPermissions,
} from '../utilities/authUtils';
import * as FirebaseUtils from '../utilities/firebaseUtils';
import { updateUserMetadata } from '../utilities/userHttp';
import Icon from './Icon';

interface SubHeaderProps {
  prefix?: string;
  v2?: boolean;
  header?: React.JSX.Element;
  tourDisabled?: boolean;
}
export const IconContainer = styled.span`
  display: inline-flex;
  height: 100%;
  align-items: center;
`;

export const ContactLink = styled.a`
  padding-left: 10px;
  padding-right: 10px;
  cursor: pointer;
  text-decoration: none;
  vertical-align: super;
  color: ${({ theme }) => theme.colors.textFive};
  font-size: 14px;
  font-weight: 600;
`;

export const TakeTourContainer = styled.div<{
  sidebarEnable?: boolean;
}>`
  display: flex;
  padding-right: ${({ sidebarEnable }) => (sidebarEnable ? '20px' : '64')};
  height: 40px;
  align-items: center;
  flex-direction: row;
  &:hover {
    text-decoration: none;
    text-decoration: underline;
    filter: brightness(0.7);
    text-decoration-color: #075c8e;
  }
`;

export const buttonReset = {
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: 0,
  outline: 'none',
  lineHeight: 1,
  padding: 8,
  fontFamily: "'proxima-nova', 'Roboto', Sans-serif",
  fontSize: 14,
  fontStyle: 'normal',
};
export const joyrideOptions: Partial<Options> = {
  arrowColor: '#fff',
  backgroundColor: '#fff',
  primaryColor: '#f04',
  textColor: '#333',
  overlayColor: 'rgba(0, 0, 0, 0.5)',
  beaconSize: 36,
  zIndex: 100000,
  width: '350px',
};

export const styles: PartialDeep<Styles> = {
  tooltip: {
    color: '#000',
    fontFamily: "'proxima-nova', 'Roboto', Sans-serif",
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    padding: '32px 32px 24px 32px',
  },
  tooltipFooter: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '16px',
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipContent: {
    padding: '0',
  },
  buttonClose: {},
  buttonPrimary: {
    ...buttonReset,
    backgroundColor: '#0091EA',
    borderRadius: '2px',
    color: '#fff',
    width: 'auto',
    height: '41px',
  },
  buttonSkip: {
    ...buttonReset,
    color: '#000',
    padding: '0px',
  },
  buttonBack: {
    ...buttonReset,
    color: '#0091EA',
    marginLeft: 'auto',
    position: 'absolute',
    padding: '0px',
  },
};

const V2Style = styled.div<{ sidebarEnable?: boolean }>`
  display: flex;
  position: ${({ sidebarEnable }) => sidebarEnable && 'relative'};
  top: ${({ sidebarEnable }) => sidebarEnable && -5};
`;

export const JoyRideCardMainText = styled.div`
  color: var(--Gray-Gray-90, #333);
  font-family: 'proxima-nova';
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.48px;
  margin-bottom: 8px;
`;

export const JoyRideCardSubText = styled.span`
  color: var(--Gray-Gray-90, #333);
  font-family: 'proxima-nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 8px;
`;

export const StyledSecondaryNavigation = styled(SecondaryNavigation)<{
  hideNavigation?: boolean;
}>`
  border-bottom: none !important;
  visibility: ${({ hideNavigation }) =>
    hideNavigation ? 'hidden' : 'visible'};

  #secondaryNav-element-0 {
    padding-left: 13px;
  }
`;

const SubHeader: FC<SubHeaderProps & HTMLAttributes<HTMLDivElement>> = ({
  prefix,
  style,
  v2,
  header,
  tourDisabled,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loadsCount = useSelector<RootState, number>(
    (state) => state.loadsCount.loadsCount
  );

  const showJoyrideButton = useSelector<RootState, boolean>(
    (state) => state.joyride.showJoyrideButton
  );

  const { showHighwayRegistrationFlow } = useSelector(
    (state: RootState) => state.showHighwayRegistrationFlow
  );

  const [startJoyRide, setStartJoyRide] = React.useState(false);

  const currentUser = getCurrentUser();
  const { data: lanes } = useGetLanesQuery(currentUser?._id);
  const userHasBookItNowPermission = getHasBookItNowPermissions();
  useGetLanesQuery(currentUser?._id, {
    skip: !currentUser?._id,
  });

  const getToolTipMsg = () => {
    let tooltipMessage = '';
    if (lanes?.length === 0 || !lanes) {
      tooltipMessage = t('Add lanes for notifications');
    } else if (lanes.length > 0 && loadsCount === 0) {
      tooltipMessage = t('No loads available');
    } else if (lanes?.length > 0 && loadsCount > 0) {
      tooltipMessage = t('Loads available');
    }
    return tooltipMessage;
  };

  const toolTipPlacement: 'bottom' | 'left' | 'right' | 'top' | undefined =
    'top';

  const handleFindLoadsClicked = () => {
    navigate(`${prefix || ''}/findLoads`);
  };

  const handleMyLanesClicked = () => {
    logMyLaneButtonClick();
    navigate(`${prefix || ''}/myLanes`);
  };

  const navtabs = [
    {
      title: t('Load_Board'),
      onClick: handleFindLoadsClicked,
    },
    {
      title: t('My Lanes'),
      onClick: handleMyLanesClicked,
      badge: {
        content: `${loadsCount}`,
        color: loadsCount > 0 ? '#0091EA' : '#808080',
        tooltipText: getToolTipMsg(),
        tooltipPlacement: toolTipPlacement,
      },
    },
  ];

  const handleJoyrideCallbacks = async (data: EventData) => {
    const { action, index, lifecycle } = data;
    if (action === 'close' || action === 'skip') {
      setStartJoyRide(false);
    } else if (lifecycle === 'complete' && index === 2 && action === 'next') {
      setStartJoyRide(false);
      if (currentUser?.email) {
        await updateUserMetadata(currentUser.email, {
          rtsProMyLanesJoyRideSeen: true,
        });
      }
    }
  };

  const rtsSteps: Step[] = [
    {
      target: '#secondaryNav',
      skipBeacon: true,
      buttons: ['back', 'close', 'primary', 'skip'],
      content: (
        <div>
          <JoyRideCardMainText>
            {t(`We made some changes.`)}
          </JoyRideCardMainText>
          <JoyRideCardSubText>
            {t(`Take a quick tour to learn more`)}.
          </JoyRideCardSubText>
        </div>
      ),
    },
    {
      target: '#secondaryNav-element-0',
      skipBeacon: true,
      content: (
        <div>
          <JoyRideCardMainText>
            {t(`New Name, Same Functions`)}.
          </JoyRideCardMainText>
          <JoyRideCardSubText>
            {t(`Search, quote and book like normal.`)}.
          </JoyRideCardSubText>
        </div>
      ),
    },
    {
      target: '#secondaryNav-element-1',
      skipBeacon: true,
      showProgress: false,
      styles: {
        buttonPrimary: {
          backgroundColor: '#72BF44',
        },
      },
      content: (
        <div>
          <JoyRideCardMainText>
            {t(`Find and book loads faster with My Lanes.`)}.
          </JoyRideCardMainText>
          <JoyRideCardSubText>
            {t(
              `The notification displays available loads on your preferred routes.`
            )}
            .
          </JoyRideCardSubText>
        </div>
      ),
    },
  ];

  const shouldShowLoadBoardTakeTour = useCallback(() => {
    if (!userHasBookItNowPermission) {
      return false;
    }
    return true;
  }, [userHasBookItNowPermission]);

  let startingIndex = 0;
  const path = pathname.toLowerCase();
  switch (path.toLowerCase()) {
    case `${prefix || ''}/findLoads`.toLowerCase(): {
      if (!shouldShowLoadBoardTakeTour()) {
        tourDisabled = true;
      }
      dispatch(setJoyrideContext('LoadBoard'));
      break;
    }
    case `${prefix || ''}/myLanes`.toLowerCase(): {
      startingIndex = 1;
      dispatch(setJoyrideContext('MyLanes'));
      break;
    }
    default: {
      dispatch(setJoyrideContext(''));
    }
  }

  const shouldShowTakeTour = useCallback(() => {
    if (tourDisabled || showHighwayRegistrationFlow) {
      return false;
    }

    return true;
  }, [tourDisabled, showHighwayRegistrationFlow]);

  return (
    <CloverThemeProvider>
      <div style={style}>
        <div style={{ paddingLeft: `${v2 ? 24 : 65}` }}>
          <StyledSecondaryNavigation
            id="secondaryNav"
            tabs={navtabs}
            startingIndex={startingIndex}
            horizontalPadding={'0'}
            hideNavigation={showHighwayRegistrationFlow}
            v2={v2}
          />
        </div>
        <Joyride
          continuous
          run={startJoyRide}
          steps={rtsSteps}
          options={{
            ...joyrideOptions,
            showProgress: true,
            dismissKeyAction: false,
            overlayClickAction: false,
            skipScroll: false,
            spotlightPadding: 0,
          }}
          styles={styles}
          onEvent={handleJoyrideCallbacks}
          locale={{
            skip: t('skip_tour'),
            next: t('Next'),
            nextWithProgress: `${t('Next')} ({current}/{total})`,
            last: `${t('Done')} (3/3)`,
            back: t('Back'),
          }}
        />
        <V2Style sidebarEnable={v2}>
          {shouldShowTakeTour() && showJoyrideButton && (
            <TakeTourContainer
              sidebarEnable={v2}
              onClick={() => {
                FirebaseUtils.logFirebaseEvent(
                  FirebaseUtils.FirebaseEvents.CLICK,
                  FirebaseUtils.FirebaseModules.FREIGHT,
                  FirebaseUtils.FirebasePages.LIST,
                  {
                    description: 'Take a tour',
                  }
                );
                dispatch(setShowJoyride(true));
              }}
            >
              <ContactLink>{t('take_a_tour')}</ContactLink>
              <IconContainer className="IconContainer">
                <Icon height={24} width={27} name="Tour" alt="take a tour" />
              </IconContainer>
            </TakeTourContainer>
          )}
          {header}
        </V2Style>
      </div>
    </CloverThemeProvider>
  );
};

export default SubHeader;
